import { Checkbox, Input, InputNumber, message, Modal, Select } from 'antd';
import axiosInstance from '../../../../../axios/axiosInstance';
import React, { useCallback, useEffect, useState } from 'react';
import { useMasterGradesSelector, useMasterSubjectsSelector } from '../../../../../redux/selector';
import { ModePage } from 'common/enum';
import { useParams } from 'react-router-dom';
import { ExerciseFormatRequest } from 'model/request';
import processError from '../../../../../utils/processError';
import { Lecture, Theory } from '../../../../../model/response';
import { findAllTheories } from '../../../../../service/theoryService';
import { findAllLectures } from '../../../../../service/lectureService';

interface Props {
  isOpen: boolean;
  onCloseModal: () => void;
  mode: ModePage;
  onBeforeSuccess: () => void;
  exerciseFormatId: string;
}

export default function ExerciseFormatFormModal({
  isOpen,
  onCloseModal,
  mode,
  onBeforeSuccess,
  exerciseFormatId,
}: Props) {
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [newExerciseFormat, setExerciseFormat] = useState<ExerciseFormatRequest | null>(null);
  const gradeList = useMasterGradesSelector();
  const subjectList = useMasterSubjectsSelector();
  const [theoryList, setTheoryList] = useState<Lecture[]>([]);
  const [lectureList, setLectureList] = useState<Theory[]>([]);
  const [lectureTheoryLoading, setLectureTheoryLoading] = useState(false);

  const fetchLectureTheory = useCallback(async () => {
    if (!newExerciseFormat?.subject || !newExerciseFormat.subject.id) {
      return;
    }
    try {
      setLectureTheoryLoading(true);
      setTheoryList(await findAllTheories(newExerciseFormat.subject.id));
      setLectureList(await findAllLectures(newExerciseFormat.subject.id));
    } catch (err) {
      processError(err);
    } finally {
      setLectureTheoryLoading(false);
    }
  }, [newExerciseFormat?.subject]);

  useEffect(() => {
    fetchLectureTheory().then();
  }, [fetchLectureTheory]);

  const onInputField = (e: any, fieldName: string) => {
    const newExerciseFormatTemp = { ...newExerciseFormat } as any;
    newExerciseFormatTemp[fieldName] = e.target.value;

    setExerciseFormat(newExerciseFormatTemp);
  };

  const onCheckboxField = (e: any, fieldName: string) => {
    const newExerciseFormatTemp = { ...newExerciseFormat } as any;
    newExerciseFormatTemp[fieldName] = e.target.checked;

    setExerciseFormat(newExerciseFormatTemp);
  };

  const onChangeSelect = (e: any, fieldName: string) => {
    const newExerciseFormatTemp = { ...newExerciseFormat } as any;
    newExerciseFormatTemp[fieldName] = {
      id: e,
    };

    setExerciseFormat(newExerciseFormatTemp);
  };

  const loadNewExerciseFormat = async () => {
    try {
      const newExerciseFormat: ExerciseFormatRequest = {
        id: '',
        name: '',
        code: '',
        lecture: undefined,
        theory: undefined,
        subject: {
          id: params.subjectId as string,
        },
        grade: {
          id: gradeList.at(0)?.id as string,
        },
        webViewActive: true,
        webViewOrder: 1,
      };
      setExerciseFormat(newExerciseFormat);
    } catch (err) {
      processError(err);
    }
  };

  const loadEditExerciseFormat = async () => {
    try {
      const res = await axiosInstance({
        url: `/exercise-formats/${exerciseFormatId}`,
        method: 'get',
      });

      if (res.status === 200) {
        setExerciseFormat(res.data);
      } else {
        message.error('Có lỗi xảy ra.');
      }
    } catch (err) {
      processError(err);
    }
  };

  const onOk = async () => {
    if (loading) {
      return;
    }
    if (!newExerciseFormat?.name) {
      message.error('Vui lòng nhập tên dạng bài tập');
      return;
    }
    if (!newExerciseFormat.subject || !newExerciseFormat.subject.id) {
      message.error('Vui lòng chọn môn học');
      return;
    }
    setLoading(true);
    try {
      if (newExerciseFormat) {
        await axiosInstance({
          url: mode === ModePage.ADD ? '/exercise-formats' : `/exercise-formats/${newExerciseFormat.id}`,
          method: mode === ModePage.ADD ? 'post' : 'put',
          data: newExerciseFormat,
        });
        onBeforeSuccess();
        message.success(mode === ModePage.ADD ? `Thêm thành công` : `Sửa thành công`);
      }
    } catch (err) {
      processError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      mode === ModePage.ADD ? loadNewExerciseFormat() : loadEditExerciseFormat();
    }
  }, [isOpen]);

  return (
    <Modal open={isOpen} onOk={onOk} onCancel={onCloseModal}>
      <div className="mt-8">
        <div className="flex items-center gap-2">
          <div className="w-[210px]">
            Tên dạng bài tập: <span className={'text-FF1D30'}>*</span>
          </div>
          <Input
            maxLength={255}
            name="name"
            onChange={(e) => onInputField(e, 'name')}
            value={newExerciseFormat?.name}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[210px]">Mã dạng bài tập:</div>
          <Input
            maxLength={255}
            name="code"
            onChange={(e) => onInputField(e, 'code')}
            value={newExerciseFormat?.code}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[140px]">
            Môn học: <span className={'text-FF1D30'}>*</span>
          </div>
          <Select
            onChange={(e) => onChangeSelect(e, 'subject')}
            className="w-[324px]"
            style={{ flex: 1 }}
            value={newExerciseFormat && newExerciseFormat.subject ? newExerciseFormat.subject.id : ''}
            options={subjectList.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[140px]">Lớp:</div>
          <Select
            onChange={(e) => onChangeSelect(e, 'grade')}
            className="w-[324px]"
            style={{ flex: 1 }}
            value={newExerciseFormat && newExerciseFormat.grade ? newExerciseFormat.grade.id : ''}
            options={gradeList.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[140px]">Lý thuyết:</div>
          <Select
            onChange={(e) => onChangeSelect(e, 'theory')}
            className="w-[324px]"
            style={{ flex: 1 }}
            value={newExerciseFormat && newExerciseFormat.theory ? newExerciseFormat.theory.id : ''}
            options={theoryList.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            loading={lectureTheoryLoading}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[140px]">Bài giảng:</div>
          <Select
            onChange={(e) => onChangeSelect(e, 'lecture')}
            className="w-[324px]"
            style={{ flex: 1 }}
            value={newExerciseFormat && newExerciseFormat.lecture ? newExerciseFormat.lecture.id : ''}
            options={lectureList.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            loading={lectureTheoryLoading}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <label>Hiển thị trên web</label> <br />
          <Checkbox checked={newExerciseFormat?.webViewActive} onChange={(e) => onCheckboxField(e, 'webViewActive')} />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <label>
            Độ ưu tiên hiển thị <span className={'text-FF1D30'}>*</span>
          </label>{' '}
          <br />
          <InputNumber
            placeholder="Nhập độ ưu tiên hiển thị..."
            value={newExerciseFormat?.webViewOrder}
            onChange={(e) =>
              newExerciseFormat &&
              setExerciseFormat({
                ...newExerciseFormat,
                webViewOrder: e || 1,
              })
            }
            required
          />
        </div>
      </div>
    </Modal>
  );
}
